import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import {
  MetricBoxConfig,
  MetricsBoxes,
} from '@/components/common/DataTable/MetricsBoxes';
import type { PaginationActions, PaginationState } from '@/hooks/usePagination';
import { TaskListMetadata } from '@/services/api/tasksService';
import { FilterValue } from '@/services/types/pagination-types';
import { selectUser } from '@/state/selectors/root-selectors';

import {
  getMetricsForVertical,
  interpolateTooltip,
  MetricDefinition,
} from '../config/metricsConfig';
import { VerticalConfig } from '../config/taskVerticals';

interface TaskMetricsBoxesProps {
  paginationState: PaginationState;
  paginationActions: PaginationActions;
  totalCount: number;
  metadata?: TaskListMetadata;
  /** Vertical configuration - used for metrics config and tooltip interpolation */
  verticalConfig: VerticalConfig;
}

/** Filter keys that metrics can modify. Excludes `ordering` (sort, not filter). */
const METRIC_FILTER_KEYS = [
  'status',
  'due_date_range',
  'due_date_before',
  'due_date_after',
  'no_due_date',
  'assignee',
  'priority',
];

/**
 * Personal task metrics. Config-driven via metricsConfig.ts.
 * Counts come from backend static_metrics (unaffected by user filters).
 */
export const TaskMetricsBoxes = ({
  paginationState,
  paginationActions,
  totalCount,
  metadata,
  verticalConfig,
}: TaskMetricsBoxesProps) => {
  const currentUser = useSelector(selectUser);

  // Extract metric counts from backend response into a lookup map
  const metricCounts = useMemo(() => {
    if (!metadata?.static_metrics) {
      return new Map<string, number>();
    }

    return new Map(
      metadata.static_metrics.map((item) => [item.category, item.count]),
    );
  }, [metadata]);

  // Get metrics configuration (vertical-specific or default)
  const metricsConfig = useMemo(
    () => getMetricsForVertical(verticalConfig.metrics),
    [verticalConfig.metrics],
  );

  // Helper to replace [CURRENT_USER_ID] token with actual user ID
  const processFilters = useCallback(
    (filters: Record<string, FilterValue>): Record<string, FilterValue> => {
      const processed: Record<string, FilterValue> = {};
      Object.entries(filters).forEach(([key, value]) => {
        if (value === '[CURRENT_USER_ID]' && currentUser?.id) {
          processed[key] = [currentUser.id];
        } else {
          processed[key] = value;
        }
      });
      return processed;
    },
    [currentUser?.id],
  );

  // Transform metric definitions into MetricBoxConfig for common component
  const boxes: MetricBoxConfig[] = useMemo(() => {
    return metricsConfig.map((metric: MetricDefinition) => {
      const count =
        metric.backendCategory === 'total'
          ? totalCount
          : (metricCounts.get(metric.backendCategory) ?? 0);

      const tooltip = interpolateTooltip(metric.tooltip, {
        vertical: verticalConfig.label,
      });

      const processedFilters = processFilters(metric.filters);

      return {
        id: metric.id,
        label: metric.label,
        count,
        tooltip,
        filters: processedFilters,
        isClearAll: metric.isClearAll,
        icon: metric.icon,
        isClickable: metric.isClickable,
      };
    });
  }, [
    metricsConfig,
    totalCount,
    metricCounts,
    verticalConfig.label,
    processFilters,
  ]);

  // Delegate to common MetricsBoxes component
  return (
    <MetricsBoxes
      boxes={boxes}
      paginationState={paginationState}
      paginationActions={paginationActions}
      metricFilterKeys={METRIC_FILTER_KEYS}
      size="compact"
      loading={!metadata?.static_metrics}
    />
  );
};
